import requests from './requests.js';
import { kebabCaseToTitleCase } from '../utils.js';

class IconMetadata {
  #toRequest = new Set();

  #requestPromise = null;

  #requesting = false;

  /**
   * IconMetadata constructor. The iconMetadata is a singleton.
   */
  constructor() {
    // Initialize an instance of icon metadata only if there is none
    if (!IconMetadata.instance) {
      this.tagTree = null;
      this.artists = null;
      this.cache = {};

      IconMetadata.instance = this;
    }
  }

  /**
   * Put an svg to the cache
   * @param {string} icon   // The cache key is the icon (format artist/icon-name)
   * @param {string} svg    // The cache data is the svg string
   */
  setCache(icon, svg) {
    this.cache[icon] = svg;
  }

  /**
   * Get an svg from cache
   * @param  {string} icon
   * @return {string}
   */
  getCache(icon) {
    return this.cache[icon] || null;
  }

  /**
   * Assign data to the icon metadata and preload application icons
   * @param {object}  tagTree       // The tag tree
   * @param {object}  icons         // The icon metadata
   * @param {object}  artists       // The artists associative array
   * @param {array}   appIconNames  // The application icon (names only)
   */
  async initialize(tagTree, icons, artists, appIconNames) {
    this.tagTree = tagTree;
    this.artists = artists;
    if (icons) {
      this.icons = {};
      Object.keys(icons).forEach(artist => {
        const artistIcons = icons[artist];
        // Change the key to format artist/icon-name
        Object.keys(artistIcons).forEach(iconName => {
          const icon = artistIcons[iconName];
          this.icons[`${artist}/${iconName}`] = icon;
        });
      });
    }

    // Preload the tag icons and the application icons
    const tagIcons = Object.values(tagTree).map(
      tag => `${tag.artist}/${tag.icon}`
    );
    const appIcons = appIconNames.map(icon => `app-icons/${icon}`);
    icons = [...tagIcons, ...appIcons];
    await this.getIcons(icons);
  }

  /**
   * Load one or more icons or get them from the cache
   * @param   {array}   icons The icons in the format artist/icon-name
   * @return  {Object}        An associative array with the icons (key: icon, value: svg-string)
   */
  async getIcons(icons) {
    let results = {};

    icons.forEach(icon => {
      // Check if the icon is already in cache
      const svg = this.getCache(icon);
      if (svg) {
        results[icon] = svg;
      } else {
        this.#toRequest.add(icon);
      }
    });

    if (this.#toRequest.size > 0) {
      if (!this.#requestPromise) {
        this.#requestPromise = this.createRequestPromise();
      }
      const response = await this.#requestPromise;
      this.#requestPromise = null;
      this.#requesting = false;
      results = { ...results, ...response };
    }

    return results;
  }

  /**
   * Creates a promise that calls the API to get the icons after a timeout
   * @returns {Promise}
   */
  createRequestPromise() {
    return new Promise((resolve, reject) => {
      setTimeout(async () => {
        this.#requesting = true;
        const toRequest = Array.from(this.#toRequest);
        this.#toRequest.clear();
        requests.assets
          .getIcons({ icons: toRequest })
          .then(response => {
            this.cache = { ...this.cache, ...response.data };
            resolve(response.data);
          })
          .catch(error => {
            console.error('Error requesting icons:', error);
            reject(error);
          });
      }, 50);
    });
  }

  /**
   * Return all the tag tree sorted by description.
   */
  get tags() {
    const orderedTree = {};
    Object.values(this.tagTree)
      .sort((a, b) => {
        if (a.tag > b.tag) return 1;
        return -1;
      })
      .forEach(value => {
        orderedTree[value.tag] = this.tagTree[value.tag];
      });
    return orderedTree;
  }

  /**
   * Return the icon of a tag in the format artist/icon-name
   * @param {string} tag
   */
  getTagIcon(tag) {
    const { icon, artist } = this.tagTree[tag];
    return `${artist}/${icon}`;
  }

  /**
   * Find icons that:
   * 1) Either have the search term in the tag
   * 2) Or the icon url (artist/icon) contains the search term
   * 3) Or the icon description contains the search term
   * @param {string} search
   */
  getIconsBySearch(search) {
    return Object.keys(this.icons).filter(
      icon =>
        icon.includes(search) ||
        this.icons[icon].tags.some(tag => tag.includes(search))
    );
  }

  /**
   * Return the name of the icon. The icon is in the format artist/icon-name
   * @param {string} icon
   */
  getIconName(icon) {
    const array = icon.split('/');
    return kebabCaseToTitleCase(array[1]);
  }

  /**
   * Return the artist of the icon. The icon is the format artist/icon-name
   * @param {string} icon
   */
  getIconArtist(icon) {
    const array = icon.split('/');
    return kebabCaseToTitleCase(array[0]);
  }

  /**
   * Return the page of the artist of the icon. The icon is the format artist/icon-name
   * @param {string} icon
   */
  getIconArtistPage(icon) {
    const array = icon.split('/');
    return this.artists[array[0]];
  }
}

const iconMetadata = new IconMetadata();
export default iconMetadata;
